import { useState, useEffect } from 'react';

interface VersionInfo {
  version: string;
  buildNumber: number;
  date: string;
  timestamp: string;
  gitSha: string;
}

// TypeScript declaration for global variables from Vite
declare const __APP_VERSION__: VersionInfo;
declare const __DEBUG_MODE__: boolean;

export function VersionDisplay() {
  const [versionInfo, setVersionInfo] = useState<VersionInfo>(__APP_VERSION__);
  const [isVisible, setIsVisible] = useState(__DEBUG_MODE__);

  // Fetch version from endpoint in production
  useEffect(() => {
    if (!__DEBUG_MODE__) {
      fetch('/version')
        .then(res => res.json())
        .then(data => setVersionInfo(data))
        .catch(() => {
          // If endpoint fails, use build-time version
          console.log('Using build-time version info');
        });
    }
  }, []);

  // Toggle visibility with keyboard shortcut (Ctrl+Shift+V)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'V') {
        e.preventDefault();
        setIsVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Don't render if not visible (toggle works in both debug and production)
  if (!isVisible) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 10,
        right: 10,
        background: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        fontFamily: 'monospace',
        zIndex: 9999,
        maxWidth: '300px',
        backdropFilter: 'blur(4px)',
        border: __DEBUG_MODE__ ? '1px solid #00ff00' : '1px solid #666',
      }}
    title={`Debug Mode: ${__DEBUG_MODE__ ? 'ON' : 'OFF'} (Ctrl+Shift+V to toggle)`}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <strong>Version Info</strong>
        {__DEBUG_MODE__ && (
          <span style={{ color: '#00ff00', fontSize: '10px' }}>DEBUG</span>
        )}
      </div>

      <div style={{ lineHeight: '1.4' }}>
        <div><strong>Version:</strong> {versionInfo.version}</div>
        {versionInfo.date && (
          <div><strong>Date:</strong> {versionInfo.date}</div>
        )}
        {versionInfo.buildNumber && (
          <div><strong>Build:</strong> #{versionInfo.buildNumber}</div>
        )}
        {versionInfo.gitSha && (
          <div><strong>Git:</strong> {versionInfo.gitSha}</div>
        )}
        {versionInfo.timestamp && (
          <div><strong>Built:</strong> {new Date(versionInfo.timestamp).toLocaleString()}</div>
        )}
        <div><strong>Mode:</strong> {__DEBUG_MODE__ ? 'Development' : 'Production'}</div>
      </div>

      {__DEBUG_MODE__ && (
        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #333', fontSize: '10px' }}>
          <div>🔍 Debug endpoints:</div>
          <div>/health - Health check</div>
          <div>/debug - Debug info</div>
          <div>/server-info - Server info</div>
          <div>/version - Version JSON</div>
        </div>
      )}

      <div style={{ marginTop: '4px', fontSize: '10px', opacity: 0.7 }}>
        Press Ctrl+Shift+V to {isVisible ? 'hide' : 'show'}
      </div>
    </div>
  );
}

// Hook to access version info anywhere in the app
export function useVersionInfo() {
  const [versionInfo, setVersionInfo] = useState<VersionInfo>(__APP_VERSION__);

  useEffect(() => {
    if (!__DEBUG_MODE__) {
      fetch('/version')
        .then(res => res.json())
        .then(data => setVersionInfo(data))
        .catch(() => {
          // Use build-time version if endpoint fails
        });
    }
  }, []);

  return versionInfo;
}

// Component for showing version in console
export function VersionConsole() {
  const versionInfo = useVersionInfo();

  useEffect(() => {
    console.group('🚀 Cantina Pay Fila - Version Information');
    console.log('Version:', versionInfo.version);
    console.log('Build Number:', versionInfo.buildNumber);
    console.log('Date:', versionInfo.date);
    console.log('Timestamp:', versionInfo.timestamp);
    console.log('Git SHA:', versionInfo.gitSha);
    console.log('Debug Mode:', __DEBUG_MODE__);
    console.log('Environment:', import.meta.env.MODE);
    console.groupEnd();

    if (__DEBUG_MODE__) {
      console.log('🐛 Debug mode is enabled');
      console.log('🔍 Available endpoints: /health, /debug, /server-info, /version');
      console.log('⌨️  Press Ctrl+Shift+V to toggle version display');
    }
  }, [versionInfo]);

  return null; // This component doesn't render anything
}